import {gql} from '@apollo/client';
import {Flex} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';
import {compose} from 'redux';

import BenchlingConfig from 'BenchlingConfig';
import {ProcedureRunDataEntryMode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import DocumentStore from 'notebook/documents/DocumentStore';
import DocumentUpdatesManager from 'notebook/realtime/DocumentUpdatesManager';
import ReviewConstants from 'notebook/reviews/ReviewConstants';
import {
  StructuredTableInsertionContext,
  StructuredTableInsertionContextValue,
} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {CrossStepFormulaBox} from 'notebook/tables/core-ui/CrossStepFormulaBox';
import spreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {createWorksheetNamespace} from 'notebook/tables/util/WorksheetTableNamespaceUtil';
import WorksheetStepFrame from 'notebook/worksheets/steps/WorksheetStepFrame';
import {areAllMaterialOutputsConfirmed} from 'notebook/worksheets/util/materialOutputsConfirmed';
import {getWorksheetControlVariables} from 'notebook/worksheets/util/worksheetControl';
import {getSelectedStepControlFlags} from 'notebook/worksheets/util/worksheetSelectedStepControl';
import {
  DocumentStatus,
  Worksheet,
  WorksheetStepDocumentSelection,
  WorksheetViewStateAction,
  WorksheetViewStateType,
} from 'notebook/worksheets/WorksheetConstants';
import {WorksheetContext, WorksheetContextValue} from 'notebook/worksheets/WorksheetContext';
import WorksheetPageHeader from 'notebook/worksheets/workspace/WorksheetPageHeader';
import WorksheetSidepanel from 'notebook/worksheets/workspace/WorksheetSidepanel';
import {
  extractStepTasksData,
  extractMethodTasksData,
  getSelectedStepFromStepId,
  getSelectedStepId,
  setSelectedStepId,
} from 'notebook/worksheets/workspace/WorksheetUtils';
import {STEP_SELECTOR_SIDEBAR_WIDTH} from 'procedures/shared-components/step-config/step-nav/StepNavigationDesignConstants';
import {useSetProcedurePMEIDocumentStoreFromWorksheet} from 'procedures/worksheets/ProcedurePMEIDocumentStore';
import {usePMEIsAndMethodTasks} from 'procedures/worksheets/ProcedureWorksheetContext';
import {areAllMethodTasksCreated} from 'procedures/worksheets/WorksheetUtils';
import RealtimeUpdateSubscriptionWrapper from 'realtime/RealtimeUpdateSubscriptionWrapper';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import TabLayout, {TabLayoutBackgroundColor} from 'ui-library/TabLayout';

import {
  WorksheetStepTasksQuery,
  WorksheetStepTasksQueryVariables,
} from './__generated/WorksheetStepTasksQuery';
import {optimisticStepStatusState} from './OptimisticStepStatusState';

export const WORKSHEET_STEP_TASKS_QUERY = gql`
  query WorksheetStepTasksQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: NOTEBOOK) {
    processTasks(ids: $ids) {
      ... on ProcessTaskNode {
        id
        status {
          ... on ProcessTaskStatusNode {
            id
            displayName
          }
        }
        taskSchema {
          ... on ProcessTaskSchemaNode {
            id
            statusLifecycle {
              id
              transitions {
                id
                outStatus {
                  id
                  displayName
                }
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
        lastStatusChange {
          ... on ProcessTaskStatusChangeNode {
            id
            creator {
              ...PartySummaryGraphQL
            }
            createdAt
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('PartySummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface WorksheetDetailsProps {
  worksheet: Worksheet;
  refetch: (isServerUpdate?: boolean) => Promise<void>;
  onSetWorksheet: (id: string) => boolean;
  isExecutionEnabled: boolean;
  dispatch: (action: WorksheetViewStateAction) => void;
  worksheetViewState: WorksheetViewStateType;
}

interface WorksheetDetailsConnectedProps extends WorksheetDetailsProps {
  hasUnsavedStepDocuments: boolean;
}

function WorksheetDetails({
  worksheet,
  refetch,
  hasUnsavedStepDocuments,
  onSetWorksheet,
  isExecutionEnabled,
  dispatch,
  worksheetViewState,
}: WorksheetDetailsConnectedProps): JSX.Element {
  const worksheetStepIds = worksheet.worksheetStepGroups.flatMap((stepGroup) =>
    stepGroup.worksheetSteps.map((step) => step.document.id)
  );
  const pmeisAndTasks = usePMEIsAndMethodTasks();
  const pmeis = pmeisAndTasks.map((pT) => pT.pmei);
  useSetProcedurePMEIDocumentStoreFromWorksheet(worksheet, pmeis);

  const [documentUpdatesManager] = useState(() => new DocumentUpdatesManager());
  const [selectedStep, setSelectedStep] = useState<WorksheetStepDocumentSelection>(() => {
    const selectedStepId = getSelectedStepId(worksheet.id, worksheetStepIds);
    return getSelectedStepFromStepId(selectedStepId || undefined, worksheet);
  });

  const tableNamespace = BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
    ? createWorksheetNamespace(worksheet.id)
    : undefined;
  const crossStepFormulaBox = useStoreSelector(
    spreadsheetStore,
    (store) => {
      if (tableNamespace) {
        const storeState = store.getState(tableNamespace);
        if (storeState.editingTable && storeState.fileId && tableNamespace && storeState.index) {
          return {
            stepId: storeState.fileId,
            tableName: storeState.editingTable,
            index: storeState.index,
            tableNamespace,
          };
        }
      }
      return null;
    },
    isEqual
  );

  const editingStep = useMemo(() => {
    if (crossStepFormulaBox) {
      for (const stepGroup of worksheet.worksheetStepGroups) {
        for (const step of stepGroup.worksheetSteps) {
          if (step.document.id === crossStepFormulaBox.stepId) {
            return step;
          }
        }
      }
    }
    return undefined;
  }, [crossStepFormulaBox, worksheet.worksheetStepGroups]);

  const setSelectedStepCallback = useCallback(
    (selectedStep: WorksheetStepDocumentSelection) => {
      const stepId = selectedStep.document?.id;
      setSelectedStep({...selectedStep, stepId});
      setSelectedStepId(worksheet.id, stepId);
    },
    [setSelectedStep, worksheet.id]
  );

  const onReturnToStep = useCallback(() => {
    if (crossStepFormulaBox) {
      let selectedStepIndex = -1;
      let selectedStepGroupIndex = -1;
      worksheet.worksheetStepGroups.forEach((stepGroup, stepGroupIndex) => {
        stepGroup.worksheetSteps.forEach((step, stepIndex) => {
          if (step.document.id === crossStepFormulaBox.stepId) {
            selectedStepIndex = stepIndex;
            selectedStepGroupIndex = stepGroupIndex;
          }
        });
      });
      if (selectedStepGroupIndex > -1 && selectedStepIndex > -1) {
        setSelectedStepCallback({
          stepIndex: selectedStepIndex,
          stepGroupIndex: selectedStepGroupIndex,
          document: {
            id: crossStepFormulaBox.stepId,
            status: DocumentStatus.LOADED,
          },
        });
      }
    }
  }, [crossStepFormulaBox, setSelectedStepCallback, worksheet.worksheetStepGroups]);

  const stepGroupIndex = selectedStep?.stepGroupIndex;
  const stepIndex = selectedStep?.stepIndex;
  const step =
    stepGroupIndex !== undefined && stepIndex !== undefined
      ? worksheet.worksheetStepGroups[stepGroupIndex].worksheetSteps[stepIndex]
      : undefined;

  const result = useBenchlingQuery<WorksheetStepTasksQuery, WorksheetStepTasksQueryVariables>(
    WORKSHEET_STEP_TASKS_QUERY,
    {
      variables: {
        ids: step?.tasks.map((task) => (isInaccessible(task) ? task.invalidId : task.id)) ?? [],
      },
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );
  const [optimisticStepStatus] = optimisticStepStatusState.useGlobalClientState(step?.document.id ?? '');
  const stepTaskData = useMemo(() => {
    const tasks = result.type === QueryResultType.SUCCESS ? result.data.processTasks : undefined;
    return optimisticStepStatus
      ? {
          ...extractStepTasksData(tasks),
          status: optimisticStepStatus,
        }
      : extractStepTasksData(tasks);
  }, [result, optimisticStepStatus]);
  const worksheetTaskData = useMemo(() => extractMethodTasksData(pmeisAndTasks), [pmeisAndTasks]);
  const enableStepTransitions = !hasUnsavedStepDocuments && isExecutionEnabled;

  // This value is passed through to the TransitionRunModal & leveraged to render an error about confirming
  // material outputs. If this disable complete run logic is ever changed, that error logic will need to be
  // updated too.
  const outputsConfirmed = areAllMaterialOutputsConfirmed(pmeis);
  const disableTransitionRunModalCompleteRunButton = !outputsConfirmed;
  const {
    isWorksheetPending,
    worksheetLastActionText,
    isWorksheetStatusBadgeDisabled,
    showWorksheetStatusDropdownButton,
    worksheetStatusBadgeTooltipText,
  } = getWorksheetControlVariables(
    worksheetTaskData,
    hasUnsavedStepDocuments,
    areAllMethodTasksCreated(pmeis, worksheet.tasks)
  );
  const isWorksheetInReview = ReviewConstants.LOCKED_STATUSES.includes(String(worksheet.review?.status));
  const {isSelectedStepActionsLocked, isSelectedStepDocumentLocked} = getSelectedStepControlFlags(
    isWorksheetPending,
    stepGroupIndex,
    stepTaskData.status,
    worksheet.worksheetStepGroups.length,
    isWorksheetInReview
  );

  // When a new step is added, we do not have the document ID for it as the document will be created on the
  // server. As such, we cannot set the document ID until the refetch is complete. This is why we set the
  // document ID to undefined when adding a new step and then set it to the correct value once the refetch
  // is complete.
  if (step?.document.id !== selectedStep.document.id && step?.document.id) {
    setSelectedStepCallback({
      ...selectedStep,
      document: {id: step.document.id, status: DocumentStatus.LOADED},
    });
  }

  const refetchStepTaskData = useCallback(async () => {
    await result.refetch();
  }, [result]);

  const worksheetContextValue: WorksheetContextValue = useMemo(
    () => ({
      deprecatedStepCreationContextId: step?.creationContextId || null,
      worksheetTaskData,
      worksheet,
      refetch,
      refetchStepTaskData,
      viewer: false,
      disableTransitionRunModalCompleteRunButton,
      isExecutionEnabled,
      dispatch,
      worksheetViewState,
    }),
    [
      step,
      worksheetTaskData,
      worksheet,
      refetch,
      refetchStepTaskData,
      disableTransitionRunModalCompleteRunButton,
      isExecutionEnabled,
      dispatch,
      worksheetViewState,
    ]
  );

  const structuredTableInsertionContextValue: StructuredTableInsertionContextValue = useMemo(
    () => ({
      itemInsertedFromId: worksheet.id,
      isProcedureMultibatchExecution:
        // All PMEIs in the worksheet should have the same parent procedure run, so we can use the first one
        pmeis.length > 0 &&
        pmeis[0].procedureRun.defaultDataEntryMode ===
          ProcedureRunDataEntryMode.ALL_CONDITION_REPLICATES_TOGETHER,
    }),
    [pmeis, worksheet.id]
  );

  useEffect(() => {
    if (selectedStep.document.id) {
      documentUpdatesManager.changeActiveDocument(selectedStep.document.id);
    }
    return () => {
      documentUpdatesManager.markInactive();
    };
  }, [selectedStep.document.id, documentUpdatesManager]);

  return (
    <RealtimeUpdateSubscriptionWrapper apiId={worksheet.id} refetch={refetch} messageFilter="no_filter">
      <WorksheetContext.Provider value={worksheetContextValue}>
        <StructuredTableInsertionContext.Provider value={structuredTableInsertionContextValue}>
          <TabLayout
            backgroundColor={TabLayoutBackgroundColor.WHITE}
            header={
              <WorksheetPageHeader
                name={worksheet.name}
                worksheetId={worksheet.id}
                isWorksheetInReview={isWorksheetInReview}
                onSetWorksheet={onSetWorksheet}
                onSetSelectedStep={setSelectedStepCallback}
                selectedStep={selectedStep}
                worksheetLastActionText={worksheetLastActionText}
                isWorksheetStatusBadgeDisabled={isWorksheetStatusBadgeDisabled || !isExecutionEnabled}
                showWorksheetStatusDropdownButton={showWorksheetStatusDropdownButton}
                worksheetStatusBadgeTooltipText={worksheetStatusBadgeTooltipText}
              />
            }
          >
            <Flex direction="row" h="100%">
              <WorksheetSidepanel
                selectedStep={selectedStep}
                onSetSelectedStep={setSelectedStepCallback}
                isWorksheetPending={isWorksheetPending}
                isWorksheetInReview={isWorksheetInReview}
              />
              {selectedStep.document.status === DocumentStatus.LOADED ? (
                <Flex direction="column" h="100%" w={`calc(100% - ${STEP_SELECTOR_SIDEBAR_WIDTH}px)`}>
                  <WorksheetStepFrame
                    stepName={step?.document.name}
                    document={selectedStep.document}
                    stepTaskData={stepTaskData}
                    tableNamespace={tableNamespace}
                    isSelectedStepActionsLocked={isSelectedStepActionsLocked || !isExecutionEnabled}
                    isSelectedStepDocumentLocked={isSelectedStepDocumentLocked || !isExecutionEnabled}
                    enableStepTransitions={enableStepTransitions}
                    isExecutionEnabled={isExecutionEnabled}
                    isWorksheetInReview={isWorksheetInReview}
                  />
                  {BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS &&
                    crossStepFormulaBox &&
                    editingStep &&
                    crossStepFormulaBox.stepId !== selectedStep.document.id && (
                      <CrossStepFormulaBox
                        stepName={editingStep.document.name}
                        stepIdBeingEdited={crossStepFormulaBox.stepId}
                        stepIdBeingDisplayed={selectedStep.document.id}
                        tableName={crossStepFormulaBox.tableName}
                        index={crossStepFormulaBox.index}
                        tableNamespace={crossStepFormulaBox.tableNamespace}
                        onReturnToStep={onReturnToStep}
                      />
                    )}
                </Flex>
              ) : selectedStep.document.status === DocumentStatus.LOADING ? (
                <CenteredHorizontalThrobber />
              ) : undefined}
            </Flex>
          </TabLayout>
        </StructuredTableInsertionContext.Provider>
      </WorksheetContext.Provider>
    </RealtimeUpdateSubscriptionWrapper>
  );
}

function mapDocumentStoreProps(
  documentStore: typeof DocumentStore,
  {worksheet}: WorksheetDetailsProps
): Partial<WorksheetDetailsConnectedProps> {
  const documentIds = worksheet.worksheetStepGroups.flatMap((stepGroup) =>
    stepGroup.worksheetSteps.map((step) => step.document.id)
  );
  const hasUnsavedStepDocuments = documentIds.some(
    (id) => documentStore.inStore(id) && documentStore.hasUnsavedChanges(id)
  );
  return {hasUnsavedStepDocuments};
}

export default compose(DocumentStore.connect(mapDocumentStoreProps))(WorksheetDetails);
